'use client'

import { useEffect, useRef, useCallback } from 'react'
import { motion, useAnimation } from 'framer-motion'
import { useNavigationStore } from '@/store/use-navigation-store'

export function LoadingBar() {
  const page = useNavigationStore((s) => s.page)
  const barControls = useAnimation()
  const containerControls = useAnimation()
  const prevPageRef = useRef(page)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const isRunningRef = useRef(false)

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t))
    timersRef.current = []
    isRunningRef.current = false
  }, [])

  useEffect(() => {
    // Skip on initial mount
    if (prevPageRef.current === page) return
    prevPageRef.current = page

    // Cancel any previous animation
    clearAllTimers()
    barControls.stop()
    containerControls.stop()

    isRunningRef.current = true

    const runAnimation = async () => {
      // Show bar
      await containerControls.start({ opacity: 1, transition: { duration: 0.1 } })
      if (!isRunningRef.current) return

      // Animate to 70%
      await barControls.start({
        width: '70%',
        transition: { duration: 0.4, ease: 'easeInOut' },
      })
      if (!isRunningRef.current) return

      // Brief pause at 70%
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 250)
        timersRef.current.push(t)
      })
      if (!isRunningRef.current) return

      // Complete to 100%
      await barControls.start({
        width: '100%',
        transition: { duration: 0.25, ease: 'easeOut' },
      })
      if (!isRunningRef.current) return

      // Pause then fade out
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 250)
        timersRef.current.push(t)
      })
      if (!isRunningRef.current) return

      await containerControls.start({ opacity: 0, transition: { duration: 0.2 } })

      // Reset for next navigation
      barControls.set({ width: '0%' })
    }

    runAnimation()

    return clearAllTimers
  }, [page, clearAllTimers, barControls, containerControls])

  return (
    <motion.div
      animate={containerControls}
      initial={{ opacity: 0, width: '100%' }}
      className="fixed top-0 left-0 z-50 h-0.5"
      role="progressbar"
      aria-label="Загрузка страницы"
    >
      <motion.div
        animate={barControls}
        initial={{ width: '0%' }}
        className="h-full bg-gradient-to-r from-primary/60 via-primary to-primary"
      />
    </motion.div>
  )
}
